import { Scorecard as ScorecardModel, ScorecardResult } from '../../models/Scorecard'
import React from 'react'
import { Accordion, Col, Row } from 'react-bootstrap'
import { ClientProfileProperty } from './ClientProfileProperty'
import { getLabel, ScorecardLabelLevel } from '../../common/clientProfile'
import { isArray } from '../../common/util'

export type ScorecardSectionProps = {
  vkey: string
  value: object
  scorecard: ScorecardModel
  scorecardResult: ScorecardResult
  schema: object
}

const getPropertySchema = (schema: { [key: string]: any }, key: string) => {
  return schema['properties'][key]
}

const getArrayPropertySchema = (schema: { [key: string]: any }, key: string) => {
  return schema['items']
}

export const ClientProfileSection: React.FC<ScorecardSectionProps> = ({ vkey, value, scorecard, scorecardResult, schema }) => {
  return (
    <div key={vkey}>
      {typeof value === 'object' && !Array.isArray(value) ? (
        <ClientProfileObjectItem vkey={vkey} value={value} scorecard={scorecard} scorecardResult={scorecardResult} schema={schema} />
      ) : (
        <ClientProfileListObjectItem vkey={vkey} values={value} scorecard={scorecard} scorecardResult={scorecardResult} schema={schema} />
      )}
    </div>
  )
}

interface ClientProfileObjectItemProps {
  vkey: string
  value: { [key: string]: any }
  scorecard: ScorecardModel
  scorecardResult: ScorecardResult
  idx?: number
  schema: { [key: string]: any }
}

const ClientProfileObjectItem: React.FC<ClientProfileObjectItemProps> = ({ vkey, value, scorecard, scorecardResult, idx, schema }) => {
  const fullKey = `${vkey}${idx !== undefined ? `[${idx}]` : ''}`

  const isNested = (obj: any) => {
    if (typeof obj !== 'object' || obj === null) {
      return false
    }

    for (const key in obj) {
      if (obj.hasOwnProperty(key) && typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        return true
      }
    }
    return false
  }

  const renderObject = (nestedKey?: string, nestedSchema?: { [key: string]: any }) => {
    const rootKey = nestedKey ? `${fullKey}.${nestedKey}` : fullKey
    const newValue = nestedKey ? value[nestedKey] : value
    const s = nestedSchema ? nestedSchema : schema
    const label = getLabel(scorecard.descriptor, rootKey, value, ScorecardLabelLevel.SECTION, scorecard.metadata)
    return (
      <Row>
        <Col>
          <Accordion defaultActiveKey={fullKey} alwaysOpen>
            <Accordion.Item eventKey={fullKey} className='mt-1'>
              <Accordion.Header className='custom-accordion-header'>
                <span className='scorecard-item-header'>{label}</span>
              </Accordion.Header>
              <Accordion.Body className='p-0'>
                {Object.entries(newValue).map(([key, value]) => {
                  if (isArray(value) && (value as Array<any>).length === 0) {
                    return null
                  }
                  return (
                    <ClientProfileProperty
                      key={`section.${rootKey}.${key}`}
                      rootKeyName={rootKey}
                      keyName={key}
                      scorecard={scorecard}
                      scorecardResult={scorecardResult}
                      sectionTitle={label}
                      schema={getPropertySchema(s, key)}
                    />
                  )
                })}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </Col>
      </Row>
    )
  }

  const renderNestedObject = () => {
    return (
      <>
        {Object.keys(value).map((key) => {
          return renderObject(key, schema['properties'][key])
        })}
      </>
    )
  }

  return <div>{isNested(value) ? renderNestedObject() : renderObject()}</div>
}

interface ClientProfileListItemProps {
  vkey: string
  values: object[]
  scorecard: ScorecardModel
  scorecardResult: ScorecardResult
  schema: object
}

const ClientProfileListObjectItem: React.FC<ClientProfileListItemProps> = ({ vkey, values, scorecard, scorecardResult, schema }) => {
  return (
    <div>
      {values.map((value, index) => {
        return (
          <ClientProfileObjectItem
            key={`${vkey}[${index}]`}
            vkey={vkey}
            idx={index}
            value={value}
            scorecard={scorecard}
            scorecardResult={scorecardResult}
            schema={getArrayPropertySchema(schema, vkey)}
          />
        )
      })}
    </div>
  )
}
